/**
 * Motor único de recomendación de batería.
 * Alineado 100% con EXPERT_BENCHMARKS (Cerebro Experto).
 * Baterías ágiles para operativos (evitar test fatigue); robustas para liderazgo/especialistas.
 */

import { normalizeSelectedTests, normalizeRoleType, type RoleType } from '@/config/tests';
import { isOfficialSelected } from '@/config/batteries';

/**
 * Obtiene los testKeys recomendados según industria y tipo de puesto.
 * Fuente única de verdad; alineado con EXPERT_BENCHMARKS.
 */
export function getRecommendedTests(industryId: string, roleId: string): string[] {
  const role = (normalizeRoleType(roleId) ?? roleId) as RoleType;
  if (!role) return ['perfil_personalidad'];

  const result: string[] = [];

  // ─── Base: perfil de personalidad (siempre) ───
  result.push('perfil_personalidad');

  // ─── Por rol: derivar de EXPERT_BENCHMARKS ───
  const roleTests = getTestsForRole(role);
  result.push(...roleTests);

  // ─── Modificador por industria ───
  if (industryId === 'hospitality_food' && role === 'customer_service') {
    if (!result.includes('criterio_servicio_cliente')) {
      result.push('criterio_servicio_cliente');
    }
  }

  const filtered = result.filter((k) => isOfficialSelected(k));
  return normalizeSelectedTests(filtered);
}

/** TestKeys por rol, derivados de EXPERT_BENCHMARKS. */
function getTestsForRole(role: RoleType): string[] {
  const tests: string[] = [];

  switch (role) {
    case 'sales_role':
      tests.push('conocimiento_puesto_ventas_retail', 'autogestion_emocional', 'RFB_SJ_8');
      break;
    case 'administrative_role':
      tests.push('JK_OFFICE_ADMIN', 'APO_6', 'estilo_trabajo_organizacion');
      break;
    case 'professional_specialist':
      tests.push('estilo_trabajo_organizacion', 'RFB_SJ_8', 'PCL_10');
      break;
    case 'customer_service':
      tests.push('autogestion_emocional', 'RFB_SJ_8');
      break;
    case 'cashier_pos':
      tests.push('PCL_10', 'APO_6', 'autogestion_emocional');
      break;
    case 'operations':
      tests.push('JK_OFFICE_ADMIN', 'PCL_10', 'APO_6');
      break;
    case 'supervision':
      tests.push('RFB_SJ_8', 'estilo_trabajo_organizacion', 'autogestion_emocional', 'PCL_10');
      break;
    case 'management':
      tests.push('RFB_SJ_8', 'autogestion_emocional', 'estilo_trabajo_organizacion', 'PCL_10');
      break;
    case 'technical':
      tests.push('RFB_SJ_8', 'estilo_trabajo_organizacion');
      break;
    case 'security':
      tests.push('PCL_10', 'autogestion_emocional');
      break;
    case 'driver_logistics':
      tests.push('PCL_10', 'autogestion_emocional', 'APO_6');
      break;
    default:
      tests.push('estilo_trabajo_organizacion', 'PCL_10');
  }

  return tests;
}
